import Image from "next/image";
import Link from "next/link";

import type { TaggedStory, User } from "../../_types/content";
import { classNames } from "./ui/Primitives";
import { CardTemplate } from "./ui/Templates";
import {
  storyCardTones,
  type CardPresentation,
  type SurfaceTone,
} from "./ui/style-config";

interface StoryLinkCardProps {
  story: TaggedStory;
  user: User;
  locationLabel?: string;
  index?: number;
  presentation?: CardPresentation;
  hrefBase?: "/test/community-wall" | "/test/stories";
  tone?: SurfaceTone;
  className?: string;
}

export default function StoryLinkCard({
  story,
  user,
  locationLabel,
  index = 0,
  presentation = "catalog",
  hrefBase = "/test/stories",
  tone,
  className,
}: StoryLinkCardProps) {
  const isCarousel = presentation === "carousel";
  const isRelated = presentation === "related";
  const isCompact = presentation === "compact";
  const cardTone = tone ?? storyCardTones[index % storyCardTones.length];
  const storyHref = `${hrefBase}/${story.slug}`;

  return (
    <CardTemplate
      presentation={presentation}
      tone={cardTone}
      className={classNames("group", className)}
      media={
        <Link
          href={storyHref}
          aria-label={`View ${story.headline}`}
          className="block h-full"
        >
          <Image
            src={story.heroImage.src}
            alt={story.heroImage.alt}
            fill
            sizes={
              isCarousel
                ? "(min-width: 1024px) 24vw, (min-width: 640px) 37vw, 71vw"
                : isRelated
                  ? "(min-width: 1280px) 31vw, (min-width: 768px) 48vw, 100vw"
                  : isCompact
                    ? "(min-width: 1024px) 14rem, 45vw"
                    : "(min-width: 1536px) 16vw, (min-width: 1280px) 20vw, (min-width: 640px) 30vw, 100vw"
            }
            className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </Link>
      }
    >
          <p className={classNames(isRelated ? "text-sm" : "text-xs", isCompact && "truncate", "font-[700] uppercase")}>
            {locationLabel ??
              `${user.profile.location.region} · ${user.profile.location.country}`}
          </p>
          <h3
            className={`${isRelated ? "text-fluid-md" : isCarousel ? "text-fluid-base" : "text-xl"} ${isCompact ? "line-clamp-3" : ""} pt-3 font-neueheitz leading-[1.05]`}
          >
            <Link href={storyHref}>{story.headline}</Link>
          </h3>
          {!isCompact ? (
            <p className={`${isRelated ? "text-lg leading-relaxed" : isCarousel ? "text-base leading-snug" : "text-sm leading-snug"} pt-4`}>
              {story.summary}
            </p>
          ) : null}

          <Link
            href={storyHref}
            className={`${isRelated ? "pt-6 text-lg" : isCompact ? "mt-auto pt-3 text-sm" : isCarousel ? "mt-auto pt-4 text-sm" : "pt-4 text-sm"} font-[700] underline-offset-4 group-hover:underline`}
          >
            Read story
          </Link>
    </CardTemplate>
  );
}
